import {
  ApplicationRef,
  Component,
  HostListener,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { SplashScreen } from '@ionic-native/splash-screen/ngx';
import { StatusBar } from '@ionic-native/status-bar/ngx';
import { Toast } from '@ionic-native/toast/ngx';
import { TranslateService } from '@ngx-translate/core';
import { merge } from 'rxjs';
import { filter, map, mergeMap } from 'rxjs/operators';

import {
  CustomService,
  DeviceDetectService,
  I18nService,
  InternetConnectionService,
  NavDataService,
  RolesService,
} from '@app/core/services';
import { environment } from '@env/environment';
import { DrupalConstants, UserService } from '@makiwin/ngx-drupal8-rest';

import { isPlatformServer } from '@angular/common';
import { SwUpdate } from '@angular/service-worker';
// import { CodePush, InstallMode, SyncStatus } from '@ionic-native/code-push/ngx';
import { FirebaseX } from '@ionic-native/firebase-x/ngx';
import { Keyboard } from '@ionic-native/keyboard/ngx';
import { MobileAccessibility } from '@ionic-native/mobile-accessibility/ngx';
import { Network } from '@ionic-native/network/ngx';
import { supportedLanguages } from '../translations/languages';
import { browserAnimationsModuleConfig } from './app.module';
import { AuthSessionService } from './core/services/auth-session/auth-session.service';
import { LocalStorageService } from './core/services/localstorage/localstorage.service';
import { ModalManagerService, ModalTypes } from './core/services/modal-manager';
import { StorageCacheService } from './core/services/storage-cache/storage-cache.service';
import { StorageDocumentsService } from './core/services/storage-documents/storage-documents.service';
import { SubscriptionPlansService } from './core/services/subscription-plans/subscription-plans.service';
import { TrackingService } from './core/services/tracking/tracking.service';

declare let wkWebView: any;
declare const cordova: any;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  readyToClose = false;
  private timesNotNetwork = 0;
  private authSessionService: AuthSessionService = inject(AuthSessionService);
  private trackingService: TrackingService = inject(TrackingService);

  constructor(
    public navDataService: NavDataService,
    private activatedRoute: ActivatedRoute,
    private titleService: Title,
    private translateService: TranslateService,
    private zone: NgZone,
    private statusBar: StatusBar,
    private splashScreen: SplashScreen,
    private i18nService: I18nService,
    private deviceDetectService: DeviceDetectService,
    private toast: Toast,
    private network: Network,
    private mobileAccessibility: MobileAccessibility,
    protected rolesService: RolesService,
    private userService: UserService,
    private customService: CustomService,
    @Inject(PLATFORM_ID) private platformId: any,
    private modalManagerService: ModalManagerService,
    private internetConnectionService: InternetConnectionService,
    private applicationRef: ApplicationRef,
    // private codePush: CodePush,
    private keyBoard: Keyboard,
    private router: Router,
    private firebaseX: FirebaseX,
    private swUpdate: SwUpdate,
    private storageCacheService: StorageCacheService,
    private storageDocumentsService: StorageDocumentsService,
    private subscriptionPlansService: SubscriptionPlansService,
    private localStorageService: LocalStorageService
  ) {
    // this.locationService.fetchMyLocation();
  }

  @HostListener('document:click', ['$event'])
  onClickHandler(event: MouseEvent) {
    const target = event.target as HTMLLinkElement;
    if (target.localName === 'a' && target.href && target.href.indexOf(window.location?.origin) === -1) {
      event.preventDefault();
      this.navDataService.navigate(target.href, true);
    }
  }

  ngOnDestroy() {
    this.i18nService.destroy();
  }

  ngOnInit() {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    this.trackingService.initCookie();

    this.internetConnectionService.isOnline$.subscribe((online) => {
      this.zone.run(() => {
        if ((this.customService.connected = online)) {
          this.timesNotNetwork = 0;
        } else {
          this.timesNotNetwork++;
        }
        // TODO: to implement a warning to show when is offline
        // if (!this.customService.connected && !this.navDataService.notificationMessage) {
        //   //  && this.timesNotNetwork >= 5) {
        //   this.navDataService.notificationMessage = {
        //     message: this.translateService.instant('No internet connection, please check your network'),
        //     type: 'danger',
        //     autoHide: false,
        //     close: true,
        //     connectionWarning: true,
        //     // icon: 'icon icon-times',
        //   };
        // } else {
        //   if (
        //     this.customService.connected &&
        //     this.navDataService.notificationMessage &&
        //     this.navDataService.notificationMessage.connectionWarning
        //   ) {
        //     delete this.navDataService.notificationMessage;
        //   }
        // }
      });
    });

    // monitor the navigation to set the active navigation item

    // Setup translations
    this.i18nService.init(environment.defaultLanguage, supportedLanguages);
    this.trackingService.initTranslationCookieBar();
    if (this.userService.isLoggedIn && this.authSessionService.currentUserProfile) {
      // const currentLanguage = this.authSessionService.currentUserProfile.preferred_langcode;
      // if (currentLanguage) {
      //   this.i18nService.language = currentLanguage.code;
      // }
      this.subscriptionPlansService.get().subscribe();
      this.rolesService.init(this.authSessionService.currentUserProfile);
      // if (this.deviceDetectService.isCordova) {
      const currentLanguage = this.authSessionService.currentUserProfile.preferred_langcode;
      if (currentLanguage) {
        this.i18nService.language = currentLanguage.code.toLowerCase();
      }
      // this.customService.trackLocation();
      // }
    }

    this.suggestLanguage();

    const onNavigationEnd = this.navDataService.router.events.pipe(filter((event) => event instanceof NavigationEnd));

    // Change page title on navigation or language change, based on route data
    merge(this.translateService.onLangChange, onNavigationEnd)
      .pipe(
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data)
      )
      .subscribe((event: any) => {
        const title = event.title;
        if (title) {
          this.titleService.setTitle(this.translateService.instant(title));
        }
      });
    // Cordova platform and plugins initialization
    if (!this.deviceDetectService.isCordova) {
      if (this.swUpdate.isEnabled) {
        if (this.swUpdate.isEnabled) {
          this.swUpdate.checkForUpdate().then((newVersion) => {
            if (newVersion) {
              this.swUpdate.activateUpdate().then(() => {
                window.location?.reload();
              });
            }
          });
        }
      }
    } else {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          for (const registration of registrations) {
            registration.unregister();
          }
        });
      }
    }
    document.addEventListener(
      'deviceready',
      () => {
        this.zone.run(() => this.onCordovaReady());
      },
      false
    );

    if (!this.authSessionService.currentUserProfile) {
      setTimeout(
        () =>
          this.authSessionService.initProfile().subscribe((profile) => {
            if (!profile && !this.navDataService.isPublicUrl(this.router.url)) {
              this.navDataService.navigate(this.deviceDetectService.isCordova ? '/user/login' : '/');
            }
          }),
        500
      );
    }
    // on window resize if we are on browser or windows platform reinit the app
    // To check
    // fromEvent(window, 'resize')
    //   .pipe(debounceTime(300))
    //   .subscribe(() => {
    //     if (
    //       this.deviceDetectService.isTabletOrientationLandscape &&
    //       this.deviceDetectService.isDesktop() &&
    //       this.navDataService.navigationHistory.indexOf('/elearning/videos') !==
    //         this.navDataService.navigationHistory.length - 1
    //     ) {
    //       this.navDataService.appReady = false;
    //       // this.modalManagerService.dismiss();
    //       setTimeout(() => {
    //         this.navDataService.appReady = true;
    //       });
    //     }
    //   });
  }

  onCordovaReady() {
    // this.codePush.sync({ installMode: InstallMode.IMMEDIATE }).subscribe((status) => {
    //   if (
    //     status === SyncStatus.DOWNLOADING_PACKAGE ||
    //     status === SyncStatus.INSTALLING_UPDATE ||
    //     status === SyncStatus.IN_PROGRESS
    //   ) {
    //     this.navDataService.notificationMessage = {
    //       message: this.translateService.instant('Downloading updates, Please wait...'),
    //       type: 'info',
    //       autoHide: false,
    //       close: false,
    //     };
    //   } else if (status === SyncStatus.UP_TO_DATE) {
    //     delete this.navDataService.notificationMessage;
    //   }
    // });
    if (this.userService.isLoggedIn) {
      // this.customService.trackLocation();
      if (!this.localStorageService.getItem('deviceInfoSubmitted')) {
        this.customService.submitDeviceInfo();
      }
    } else {
      this.i18nService.language = window.navigator?.language;
    }
    this.deviceDetectService.enableOrientation();
    // this.initGTM();
    this.splashScreen.hide();
    this.storageCacheService.initCacheDirectory();
    this.mobileAccessibility.usePreferredTextZoom(false);

    this.checkNetworkConnection();
    this.statusBar.styleLightContent();
    this.statusBar.overlaysWebView(false);
    this.statusBar.backgroundColorByHexString('#005EB8');
    // handle back button
    this.handleBackButton();

    if (this.deviceDetectService.isIOS) {
      browserAnimationsModuleConfig.disableAnimations = true;
      wkWebView.injectCookie(DrupalConstants.backEndUrl + '/');
      this.keyBoard.hideFormAccessoryBar(false);
      this.trackingService.initIDFA();
      this.storageDocumentsService.storageLocation = cordova.file.documentsDirectory;
      // WEB-3990 - fix scroll active element on the viewPort on iOS
      window.addEventListener('keyboardDidShow', () => {
        document.activeElement?.scrollIntoView();
      });
    } else {
      this.storageDocumentsService.storageLocation = cordova.file.externalRootDirectory + '/Download';
      // this.storageDocumentsService.storageLocation = cordova.file.externalDataDirectory;
    }
    this.storageDocumentsService.setDownloadEntry();

    // using language suggest for web and cordova
    // if (!localStorage.getItem('mainLanguage')) {
    //   // set app default language
    //   this.setPreferredLanguage();
    // }

    document.addEventListener(
      'resume',
      () => {
        setTimeout(() => {
          this.zone.run(() => {
            this.authSessionService.initProfile().subscribe();
          });
        }, 0);
      },
      false
    );
    this.firebaseX.onMessageReceived().subscribe((notification) => {
      if (notification.url_to_show && notification.tap) {
        this.navDataService.navigate(notification.url_to_show, true);
      }
    });
  }

  checkNetworkConnection() {
    // watch network for a disconnect
    this.network.onDisconnect().subscribe(() => {
      this.customService.connected = false;
    });

    // watch network for a connection
    this.network.onConnect().subscribe(() => {
      this.customService.connected = true;
    });
  }

  /**
   * suggest the default app language on first start
   */
  suggestLanguage() {
    const defaultLang = navigator.language.split('-')[0];
    // only once, browser language supported, not equals the current language
    if (
      !this.deviceDetectService.isCordova &&
      !this.localStorageService.getItem('language_suggested') &&
      this.i18nService.getSupportedLanguage(defaultLang) &&
      defaultLang !== this.i18nService.language
    ) {
      setTimeout(() => {
        this.modalManagerService.open(ModalTypes.languageSuggest).subscribe({
          next: (reason) => {
            if (reason === -1) this.localStorageService.setItem('language_suggested', this.i18nService.language);
          },
        });
      }, 5000);
    }
    // if (this.userService.isLoggedIn) {
    //   const uid = DrupalConstants.Connection.current_user.uid;
    //   const values: any = {
    //     preferred_langcode: this.i18nService.language.toLowerCase(),
    //   };
    //   this.userService.update(uid, values).subscribe();
    // }
  }

  /**
   * Override cordova android back btn
   * navigate back if not on home page, and close app if on home page after displaying a confirm message
   */
  handleBackButton() {
    // exit app if user at home, otherwhise will work as a normal back btn
    document.addEventListener(
      'backbutton',
      (e: any) => {
        e.preventDefault();
        if (this.navDataService.router.url.indexOf('/account') !== -1) {
          if (this.readyToClose) {
            (navigator as any).app.exitApp();
          } else {
            this.toast
              .show(this.translateService.instant(`Press back again to exit`), '3000', 'bottom')
              .subscribe((toast) => {
                if (toast.event) {
                  this.readyToClose = false;
                }
              });
            this.readyToClose = true;
          }
        } else {
          this.zone.run(() => {
            this.navDataService.navigateBack();
            setTimeout(() => {
              this.applicationRef.tick();
            }, 0);
          });
        }
        return false;
      },
      false
    );
  }
}
